import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  signal,
  DestroyRef,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { startWith } from 'rxjs';
import { CheckToggleIconComponent } from '@kuiper/icons';
import { BadgeComponent } from '../../../ui-components/lib/badge/badge.component';
import { inputTypesEnum } from '../../enums/input-type';
import { RequestFormGroup } from '../../interface/request-form-builder.model';
import { UploadFilesUrlInterface } from '../../interface/upload-files-url.model';
import { ShowConditionSectionPipe } from '../../pipes/show-condition-section/show-condition-section.pipe';
import { ShowConditionPipe } from '../../pipes/show-condition/show-condition.pipe';
import { ShowHideFieldPipe } from '../../pipes/show-hide-field-pipe/show-hide-field.pipe';
import { CompositeFieldComponent } from '../composite-field/composite-field.component';
import { DynamicTableInputComponent } from '../dynamic-table-input/dynamic-table-input.component';
import { FormBuilderFieldComponent } from '../form-builder-field/form-builder-field.component';
import { FormBuilderMultiFieldComponent } from '../form-builder-multi-field/form-builder-multi-field.component';
import { ExpandCollapseAllComponent } from '../../../ui-components/lib/expand-collapse-all/expand-collapse-all.component';
import { SectionHeaderComponent } from '../../../core/common/components/section-header/section-header.component';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-form-builder',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ShowConditionSectionPipe,
    BadgeComponent,
    CheckToggleIconComponent,
    FormBuilderFieldComponent,
    ShowConditionPipe,
    ShowHideFieldPipe,
    FormBuilderMultiFieldComponent,
    DynamicTableInputComponent,
    CompositeFieldComponent,
    SectionHeaderComponent,
    ExpandCollapseAllComponent,
  ],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FormBuilderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  isExpandCollapseSectionsEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.form_builder_expand_collapse_sections
    )
  );

  group = input.required<RequestFormGroup[]>();
  form = input.required<FormGroup>();
  sectionOriginalToggleArray = signal<boolean[]>([]);
  inputTypesEnum = inputTypesEnum;
  uploadFileUrls = input<UploadFilesUrlInterface>({ upload: '', remove: '' });
  isAllSectionsExpanded = signal<boolean>(true);

  /**
   * FormGroup identity is stable across value changes. Tracking values as a
   * signal forces show-condition bindings to re-evaluate (including after
   * async patchValue/setValue from lookups), which impure pipes alone cannot
   * guarantee when change detection is not re-triggered.
   */
  protected readonly formRevision = signal<unknown>(null);

  constructor() {
    effect(onCleanup => {
      const form = this.form();
      const subscription = form.valueChanges
        .pipe(
          startWith(form.getRawValue()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(value => this.formRevision.set(value));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.setSectionOriginalToggleArray();
  }

  setSectionOriginalToggleArray() {
    this.group().forEach((_, index) => {
      // Initialize all sections to show new data first (false = new data, true = original data)
      this.sectionOriginalToggleArray()[index] = false;
    });
  }

  changeOriginalUpdatedToggle(index: number) {
    this.sectionOriginalToggleArray()[index] =
      !this.sectionOriginalToggleArray()[index];
  }
}
